/**
 * Google encoded-polyline (precision 5) decoding.
 *
 * Why this is hand-written rather than a dependency: the roadmap's correctness rests on
 * this function (AC-4.12 — draw the road, not a straight line), it is ~40 lines, and a
 * dependency here would be the smallest thing in the tree carrying the largest claim.
 * It is verified against real captured OSRM output in polyline.test.ts.
 *
 * Format: https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 *
 * Each coordinate delta is a chunked varint, zig-zag encoded:
 *   1. subtract 63 from a char code to get a 6-bit chunk; the low 5 bits are data
 *   2. keep reading while the chunk is >= 0x1f (bit 5 is the continuation flag)
 *   3. the accumulated value is zig-zag: an odd value means the delta was negative
 *
 * Precision 5 means coordinates are stored in units of 1e-5 degrees (~1.1 m at the
 * equator), finer than OSRM's simplified road geometry ever needs.
 */

/** A decoded coordinate as `[lat, lon]` — the order map SDKs and OSRM's GeoJSON use. */
export type LatLon = readonly [lat: number, lon: number];

/** OSRM's `geometries=polyline` uses precision 5. */
const OSRM_PRECISION = 5;

/** Cursor over the encoded string; a plain object so reads advance it in place. */
interface Cursor {
  i: number;
}

/**
 * Read one zig-zag varint and advance the cursor past it.
 *
 * Kept as a single helper because latitude and longitude are encoded identically —
 * duplicating this loop is how the two halves quietly drift apart.
 *
 * The continuation threshold is 0x20, not 0x1f, and getting that wrong is a real bug this
 * function had: a terminating chunk whose 5-bit payload is exactly 31 (0b11111) encodes as
 * 31, and `>= 0x1f` misreads it as "more chunks follow", desynchronising every subsequent
 * point. The encoder emits continuation chunks as `0x20 | payload` (32..63) and the final
 * chunk as a bare payload (0..31), so the test is `>= 0x20`.
 *
 * It went unnoticed on short routes because payload 31 is uncommon; it appeared on a
 * 25 km leg with 73 points. polyline.test.ts now compares this decoder against the
 * provider's own GeoJSON for the same route, which is ground truth from a different code
 * path and would have caught it immediately.
 */
function readDelta(encoded: string, cursor: Cursor): number {
  let result = 0;
  let shift = 0;
  let chunk: number;

  do {
    if (cursor.i >= encoded.length) {
      throw new RangeError("encoded polyline ended mid-value");
    }
    const code = encoded.charCodeAt(cursor.i++);
    // 63..126 are the only valid characters; anything else means this is not a polyline.
    if (code < 63 || code > 126) {
      throw new RangeError(`invalid polyline character ${JSON.stringify(encoded[cursor.i - 1])} at index ${cursor.i - 1}`);
    }
    chunk = code - 63;
    result |= (chunk & 0x1f) << shift;
    shift += 5;
  } while (chunk >= 0x20);

  return result & 1 ? ~(result >> 1) : result >> 1;
}

/**
 * Decode an encoded polyline into `[lat, lon]` pairs.
 *
 * The result is frozen: route geometry is reference data, and a mutated coordinate is a
 * bug that is very hard to see on a map.
 *
 * @throws RangeError if the input is truncated mid-value or contains a character outside
 *         the encoding (including a '\\' escape, which appears in some encoders' output).
 *         Throwing matters: returning a partial path would render a route that stops in
 *         the middle of nowhere, which reads as a data problem rather than a decode bug.
 */
export function decodePolyline(encoded: string, precision: number = OSRM_PRECISION): readonly LatLon[] {
  if (!Number.isInteger(precision) || precision < 0 || precision > 9) {
    throw new RangeError(`precision must be an integer in [0, 9], got ${precision}`);
  }

  const factor = 10 ** precision;
  const cursor: Cursor = { i: 0 };
  const coords: LatLon[] = [];
  let lat = 0;
  let lon = 0;

  while (cursor.i < encoded.length) {
    lat += readDelta(encoded, cursor);
    lon += readDelta(encoded, cursor);
    coords.push([lat / factor, lon / factor] as const);
  }

  return Object.freeze(coords);
}

/**
 * Encode one zig-zag varint. The exact inverse of `readDelta`, and the pair is what lets a
 * multi-piece path be reassembled into one string.
 */
function writeDelta(value: number, out: string[]): void {
  // Zig-zag: negatives take the odd values, so a sign costs no extra byte.
  let remaining = value < 0 ? ~(value << 1) : value << 1;
  while (remaining >= 0x20) {
    out.push(String.fromCharCode((0x20 | (remaining & 0x1f)) + 63));
    remaining >>= 5;
  }
  out.push(String.fromCharCode(remaining + 63));
}

/**
 * Encode `[lat, lon]` pairs as a precision-5 polyline.
 *
 * Written alongside the decoder rather than pulled from a dependency, for the same reason the
 * decoder is hand-written: it is the inverse of a function the roadmap's correctness rests on,
 * and a round trip through both is a property a test can hold it to. It exists because a path
 * now arrives in pieces — OSRM's step geometries, and a day's own legs — and a piece boundary
 * is a new origin, so the pieces cannot be concatenated as strings: each piece re-states its
 * first point as an absolute coordinate, which a decoder reading the joined string would take
 * as a delta from the previous piece's last point.
 *
 * @throws RangeError if a coordinate is not finite, because `NaN` would otherwise encode as a
 *         valid-looking string that decodes to a nonsense position.
 */
export function encodePolyline(points: readonly LatLon[], precision: number = OSRM_PRECISION): string {
  if (!Number.isInteger(precision) || precision < 0 || precision > 9) {
    throw new RangeError(`precision must be an integer in [0, 9], got ${precision}`);
  }

  const factor = 10 ** precision;
  const out: string[] = [];
  let lat = 0;
  let lon = 0;

  for (const point of points) {
    const [pointLat, pointLon] = point;
    if (!Number.isFinite(pointLat) || !Number.isFinite(pointLon)) {
      throw new RangeError(`polyline point [${String(pointLat)}, ${String(pointLon)}] is not finite`);
    }
    const nextLat = Math.round(pointLat * factor);
    const nextLon = Math.round(pointLon * factor);
    writeDelta(nextLat - lat, out);
    writeDelta(nextLon - lon, out);
    lat = nextLat;
    lon = nextLon;
  }

  return out.join("");
}

/**
 * Join contiguous pieces of one road into a single encoded path.
 *
 * The pieces are encoded strings that each begin at their own origin — consecutive OSRM steps of
 * one leg, or a day's legs — so they are decoded, joined, and re-encoded as one path. A piece whose
 * first point repeats the running path's last point has that duplicate dropped; it is the
 * maneuver/junction the two pieces share, and a zero-length segment there is noise, not a road.
 *
 * `null` is returned, not a bridge, when the pieces are **not** contiguous: joining an end to an
 * unrelated start would draw a straight line between them, which is the substitution this codebase
 * refuses everywhere else — a road nobody drove. Null means "no drawable path", which the map
 * already renders as pins without a line.
 */
export function stitchPolylines(
  pieces: readonly (string | null | undefined)[],
  precision: number = OSRM_PRECISION,
): string | null {
  const points: LatLon[] = [];

  for (const piece of pieces) {
    if (typeof piece !== "string" || piece === "") continue;
    const decoded = decodePolyline(piece, precision);
    if (decoded.length === 0) continue;
    const first = decoded[0]!;
    let start = 0;
    if (points.length > 0) {
      const last = points[points.length - 1]!;
      if (last[0] !== first[0] || last[1] !== first[1]) return null;
      start = 1;
    }
    for (let i = start; i < decoded.length; i++) points.push(decoded[i]!);
  }

  return points.length === 0 ? null : encodePolyline(points, precision);
}

/**
 * Great-circle distance in metres between two `[lat, lon]` points.
 *
 * Answers the question the roadmap's honesty rests on: how much longer is the road than
 * the straight line? Measured on the Đà Lạt reference day, 2.24x — which is why drawing
 * straight lines between stops would misrepresent the journey.
 */
export function haversineMetres(a: LatLon, b: LatLon): number {
  const EARTH_RADIUS_M = 6_371_008.8;
  const toRad = Math.PI / 180;
  const dLat = (b[0] - a[0]) * toRad;
  const dLon = (b[1] - a[1]) * toRad;
  const lat1 = a[0] * toRad;
  const lat2 = b[0] * toRad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/**
 * Length of a decoded path in metres.
 *
 * A simplified polyline is shorter than the true road (OSRM drops geometrically redundant
 * points), so this is a lower bound. Use it to assert a stored path *resembles* the
 * reported distance — never to display a distance, which must come from the provider.
 */
export function pathLengthMetres(path: readonly LatLon[]): number {
  let total = 0;
  for (let i = 1; i < path.length; i++) {
    total += haversineMetres(path[i - 1]!, path[i]!);
  }
  return total;
}
